import {
  createSlice, PayloadAction, createAsyncThunk,
} from '@reduxjs/toolkit';
import { doGetRequest, doPutRequest, fetchMapAddress, fetchMapCoordinate } from '../../../common/request';
import { convertCoordinateFromString, convertCoordinateToString, getTimeRange } from '../../../common/utils';
import { appConfig } from '../../../common/config';
import { RootState } from '../../../store';
import { isLevelSense, validateLevelDistances } from '../../../common/helper';

interface DeviceState {
  // PIP Device
  device?: any;
  coordinate: string;
  address: string;
  isSyncMap: boolean;
  assetName: string;
  emptyDistance: string;
  fullDistance: string;
  isLoading: boolean;
  errMsg?: string;

  // Recent events
  recentEvents?: any[];

  // Sigfox device
  sigfoxDevice?: any;
  isLoadingSigfoxDevice?: boolean;
  sigfoxDeviceErrMsg?: string;
}

const initialState: DeviceState = {
  coordinate: '',
  address: '',
  isSyncMap: true,
  assetName: '',
  emptyDistance: '',
  fullDistance: '',
  isLoading: false,
};

// Handle actions in your reducers
const deviceSlice = createSlice({
  name: 'device',
  initialState,
  reducers: {
    setLoadingDevice: (state) => {
      state.isLoading = true;
      state.device = undefined;
    },
    setDevice: (state, action: PayloadAction<any>) => {
      state.device = action.payload;
      state.isLoading = false;
      state.address = action.payload?.InstallationData?.AdditionalData?.Address || '';
      state.coordinate = convertCoordinateToString(action.payload?.InstallationData?.Latitude, action.payload?.InstallationData?.Longitude);
      state.assetName = action.payload?.InstallationData?.AdditionalData?.Name || '';
      state.emptyDistance = (action.payload?.InstallationData?.EmptyDistance || '').toString();
      state.fullDistance = (action.payload?.InstallationData?.FullDistance || '').toString();
    },
    setRecentEvents: (state, action: PayloadAction<any[]>) => {
      state.recentEvents = action.payload;
    },
    setError(state, action: PayloadAction<string>) {
      state.isLoading = false;
      state.device = undefined;
      state.errMsg = action.payload;
    },
    setCoordinate(state, action: PayloadAction<string>) {
      state.coordinate = action.payload;
    },
    setAddress(state, action: PayloadAction<string>) {
      state.address = action.payload;
    },
    setSyncMap(state, action: PayloadAction<boolean>) {
      state.isSyncMap = action.payload;
    },
    setAssetName(state, action: PayloadAction<string>) {
      state.assetName = action.payload;
    },
    setEmptyDistance(state, action: PayloadAction<string>) {
      state.emptyDistance = action.payload;
    },
    setFullDistance(state, action: PayloadAction<string>) {
      state.fullDistance = action.payload;
    },
    // Sigfox device
    setLoadingSigfoxDevice: (state) => {
      state.isLoadingSigfoxDevice = true;
      state.sigfoxDevice = undefined;
      state.sigfoxDeviceErrMsg = undefined;
    },
    setSigfoxDevice: (state, action: PayloadAction<any>) => {
      state.isLoadingSigfoxDevice = false;
      state.sigfoxDevice = action.payload;
      state.sigfoxDeviceErrMsg = undefined;
    },
    setSigfoxDeviceError(state, action: PayloadAction<string>) {
      state.isLoadingSigfoxDevice = false;
      state.sigfoxDevice = undefined;
      state.sigfoxDeviceErrMsg = action.payload;
    },
  },
});

export const fetchDevice = createAsyncThunk<void, {deviceIdOrSerial?: string}>(
  'device/fetchDevice',
  async (params, thunkAPI) => {
    try {
      thunkAPI.dispatch(deviceSlice.actions.setDevice(undefined));
      if (!params.deviceIdOrSerial) {
        return;
      }
      thunkAPI.dispatch(deviceSlice.actions.setLoadingDevice());
      const device = await doGetRequest(`${appConfig.settings.pipApiBase}/devices/${params.deviceIdOrSerial}`, false);
      thunkAPI.dispatch(deviceSlice.actions.setDevice(device));
      thunkAPI.dispatch(fetchSigfoxDevice({customerId: device.CustomerId, networkId: device.NetworkId}));
    } catch (err) {
      thunkAPI.dispatch(deviceSlice.actions.setError('Device not found'));
    }
  },
);

export const fetchSigfoxDevice = createAsyncThunk<void, {customerId: string, networkId: string}>(
  'device/fetchSigfoxDevice',
  async (params, thunkAPI) => {
    thunkAPI.dispatch(deviceSlice.actions.setSigfoxDevice(undefined));
    const customerId = params.customerId;
    const networkId = params.networkId;
    if (!customerId || !networkId) {
      console.error('Invalid customer Id or network Id');
      return;
    }
    // Fetch Sigfox device
    thunkAPI.dispatch(deviceSlice.actions.setLoadingSigfoxDevice());
    try {
      const device = await doGetRequest(`${appConfig.settings.pipApiBase}/customers/${customerId}/sigfox/networkId/${networkId}`, false);
      thunkAPI.dispatch(deviceSlice.actions.setSigfoxDevice(device));
    } catch (err) {
      thunkAPI.dispatch(deviceSlice.actions.setSigfoxDeviceError('Failed to fetch the device from Sigfox. Error: ' + (err as any).message));
    }
  },
);

export const fetchRecentEvents = createAsyncThunk<void, {secondsUntilNow: number}>(
  'device/fetchRecentEvents',
  async (params, thunkAPI) => {
    // Get device from state
    const device = (thunkAPI.getState() as RootState).device?.device;
    if (!device) {
      return;
    }
    const deviceId = device.DeviceId;
    const customerId = device.CustomerId;
    if (!deviceId || !customerId) {
      console.error('Invalid device');
    }
    // Fetch recent events
    try {
      const timeRange = getTimeRange(params.secondsUntilNow);
      let queryParams = `?deviceIds=${deviceId}&fromUtc=${timeRange.from}&toUtc=${timeRange.to}`;
      const events = await doGetRequest(`${appConfig.settings.pipApiBase}/customers/${customerId}/events${queryParams}`);
      thunkAPI.dispatch(deviceSlice.actions.setRecentEvents(events));
    } catch (err) {
      console.error(err);
    }
  },
);

export const installDevice = createAsyncThunk<void>(
  'device/installDevice',
  async (_, thunkAPI) => {
    // Get device from state
    const deviceState = (thunkAPI.getState() as RootState).device;
    const device = deviceState?.device;
    if (!device) {
      console.error('Not found device');
      return;
    }
    const deviceId = device.DeviceId;
    const customerId = device.CustomerId;
    const serialNumber = device.SerialNumber;
    if (!deviceId || !customerId || !serialNumber) {
      console.error('Invalid device');
    }
    // Save device
    try {
      const coordinate = convertCoordinateFromString(deviceState.coordinate.trim());
      const address = deviceState.address.trim();
      const assetName = deviceState.assetName.trim();
      const emptyDistance = deviceState.emptyDistance.trim();
      const fullDistance = deviceState.fullDistance.trim();

      if (!coordinate || !address || !assetName || (isLevelSense(device.DeviceTypeName) && !validateLevelDistances(emptyDistance, fullDistance))) {
        console.error('Invalid coordinate, address, asset name or container distances');
        return;
      }

      const body: any = {
        SerialNumber: serialNumber,
        GpsLocation: {
          Latitude: coordinate.lat,
          Longitude: coordinate.lng,
        },
        AdditionalData: {
          Name: assetName,
          Address: address,
        },
        SkipNetworkInstallation: true,
      }

      if (isLevelSense(device.DeviceTypeName)) {
        body.EmptyDistance = emptyDistance.length > 0 ? parseFloat(emptyDistance) : null;
        body.FullDistance = fullDistance.length > 0 ? parseFloat(fullDistance) : null;
      }

      await doPutRequest(`${appConfig.settings.pipApiBase}/customers/${customerId}/devices/install`, body);
    } catch (err) {
      console.error(err);
    }
  },
);

export const changeCoordinate = createAsyncThunk<void, string>(
  'device/changeCoordinate',
  async (coordinate, thunkAPI) => {
    // Get device from state
    const deviceState = (thunkAPI.getState() as RootState).device;
    const device = deviceState?.device;
    if (!device) {
      console.error('Not found device');
      return;
    }
    
    try {
      // Update coordinate
      thunkAPI.dispatch(deviceSlice.actions.setCoordinate(coordinate));
      // Update address if sync map is enabled
      if (deviceState.isSyncMap) {
        const address = await fetchMapAddress(coordinate);
        thunkAPI.dispatch(deviceSlice.actions.setAddress(address));
      }
    } catch (err) {
      thunkAPI.dispatch(deviceSlice.actions.setError('Address not found'));
      console.error(err);
    }
  },
);

export const changeAddress = createAsyncThunk<void, string>(
  'device/changeAddress',
  async (address, thunkAPI) => {
    // Get device from state
    const deviceState = (thunkAPI.getState() as RootState).device;
    const device = deviceState?.device;
    if (!device) {
      console.error('Not found device');
      return;
    }

    try {
      // Update address
      thunkAPI.dispatch(deviceSlice.actions.setAddress(address));
      // Update coordinate if sync map is enabled
      if (deviceState.isSyncMap) {
        const coordinate = await fetchMapCoordinate(address);
        thunkAPI.dispatch(deviceSlice.actions.setCoordinate(coordinate));
      }
    } catch (err) {
      thunkAPI.dispatch(deviceSlice.actions.setError('Coordinate not found'));
      console.error(err);
    }
  },
);

export const {
  setAssetName,
  setSyncMap,
  setEmptyDistance,
  setFullDistance,
} = deviceSlice.actions;

export default deviceSlice.reducer;
